import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react';
import type {
  AnalyticsEventMap,
  AnalyticsEventName,
  AnalyticsProvider,
} from '@polybank/shared';
import { createAnalyticsProvider, hashUserId } from '@/analytics';
import { useAuth } from './auth-context';

interface AnalyticsContextValue {
  trackEvent: <E extends AnalyticsEventName>(
    name: E,
    data: AnalyticsEventMap[E]
  ) => void;
}

const AnalyticsContext = createContext<AnalyticsContextValue | undefined>(
  undefined
);

interface AnalyticsProviderWrapperProps {
  children: ReactNode;
}

export function AnalyticsProviderWrapper({
  children,
}: AnalyticsProviderWrapperProps) {
  const providerRef = useRef<AnalyticsProvider | null>(null);
  const { user } = useAuth();

  // Create and init provider once
  if (!providerRef.current) {
    const provider = createAnalyticsProvider();
    provider.init({
      enabled: import.meta.env.PROD,
      debug: import.meta.env.DEV,
    });
    providerRef.current = provider;
  }

  // Identity management: identify on login, reset on logout
  useEffect(() => {
    const provider = providerRef.current;
    if (!provider) return;

    if (user?.$id) {
      provider.identify({ anonymousId: hashUserId(user.$id) });
    } else {
      provider.resetIdentity();
    }
  }, [user?.$id]);

  const trackEvent = useCallback(
    <E extends AnalyticsEventName>(name: E, data: AnalyticsEventMap[E]) => {
      providerRef.current?.trackEvent(name, data);
    },
    []
  );

  const value: AnalyticsContextValue = { trackEvent };

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
}

export function useAnalytics(): AnalyticsContextValue {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error(
      'useAnalytics must be used within an AnalyticsProviderWrapper'
    );
  }
  return context;
}
